import { useCallback, useEffect, useRef, useState } from 'react'
import type { SentenceAnalysis, PosKey } from '@/types/sentence'
import { Spinner } from '@/components/ui/Spinner'
import { PosLegend } from './PosLegend'
import { WordChips } from './WordChips'
import { ArcOverlay, ARC_PADDING_TOP } from './ArcOverlay'
import { TokenDetail } from './TokenDetail'

interface SentenceAnalyzerProps {
  open: boolean
  onClose: () => void
  sentence: string
  analysis: SentenceAnalysis | null
  loading: boolean
}

export function SentenceAnalyzer({ open, onClose, sentence, analysis, loading }: SentenceAnalyzerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const arcContainerRef = useRef<HTMLDivElement>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null)
  const [chipsWrapped, setChipsWrapped] = useState(false)

  const activeIndex = pinnedIndex ?? hoveredIndex

  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      panelRef.current?.focus()
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Reset state when analysis changes
  useEffect(() => {
    setHoveredIndex(null)
    setPinnedIndex(null)
  }, [analysis])

  const handleHover = useCallback((index: number | null) => {
    if (pinnedIndex !== null) return
    setHoveredIndex(index)
  }, [pinnedIndex])

  const handleClick = useCallback((index: number) => {
    setPinnedIndex((prev) => (prev === index ? null : index))
    setHoveredIndex(null)
  }, [])

  if (!open) return null

  const tokens = analysis?.tokens ?? []
  const arcs = analysis?.arcs ?? []
  const usedPos = [...new Set(tokens.map((t) => t.pos))] as PosKey[]
  const activeToken = activeIndex !== null ? tokens[activeIndex] ?? null : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-12 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Sentence analysis"
    >
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full max-w-4xl rounded-2xl border border-border-divider bg-card shadow-xl outline-none"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-6 px-4 pt-6 pb-2 sm:px-10 sm:pt-10">
          <div className="min-w-0">
            <p className="text-[11px] font-light uppercase tracking-widest text-muted mb-2">
              Sentence analysis
            </p>
            <p className="font-serif text-xl sm:text-2xl font-medium text-heading leading-snug break-words">
              {sentence}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 mt-1 p-2 min-h-11 min-w-11 flex items-center justify-center rounded-lg text-muted hover:bg-surface-bg transition-colors duration-200"
            aria-label="Close"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-6 pt-4 sm:px-10 sm:pb-10">
          {loading && (
            <div className="flex items-center gap-2 text-muted text-sm font-light py-16 justify-center">
              <Spinner />
              Analyzing sentence...
            </div>
          )}

          {!loading && analysis && tokens.length > 0 && (
            <div className="space-y-8">
              <PosLegend usedPos={usedPos} />

              <div
                ref={arcContainerRef}
                className="relative"
                style={{ paddingTop: chipsWrapped ? 16 : ARC_PADDING_TOP }}
              >
                <ArcOverlay
                  tokens={tokens}
                  arcs={arcs}
                  activeIndex={activeIndex}
                  containerRef={arcContainerRef}
                  onWrappedChange={setChipsWrapped}
                />
                <WordChips
                  tokens={tokens}
                  arcs={arcs}
                  activeIndex={activeIndex}
                  onHover={handleHover}
                  onClick={handleClick}
                />
              </div>

              <TokenDetail token={activeToken} />
            </div>
          )}

          {!loading && analysis && tokens.length === 0 && (
            <p className="text-sm font-light text-muted py-12 text-center">
              Could not parse this sentence. Try a different selection.
            </p>
          )}

          {!loading && !analysis && (
            <p className="text-sm font-light text-muted py-12 text-center">
              Analysis failed. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
